import { useEffect, useRef, useState } from 'react';
import { SwipaWordmark } from '@/icons';
import { Link, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Fingerprint } from 'lucide-react';
import { useAuthStore } from '@/store/authStore';
import { extractError, extractApiError } from '@/api/client';
import { authApi } from '@/api/auth';
import { webauthnApi } from '@/api/webauthn';
import TotpModal from '@/components/auth/TotpModal';
import { getSafeReturnTo, withReturnTo } from '@/lib/safeReturnTo';

const schema = z.object({
  // Нормализуем в lower-case до отправки — backend искал бы '@email.ru' / '@Email.ru'
  // как разные значения, что приводит к необъяснимым «Неверный логин/пароль»
  // когда юзер случайно нажал Caps Lock или начал с заглавной (мобильная клавиатура).
  // Пароль НЕ нормализуем — он чувствителен к регистру by design.
  emailOrUsername: z.string().trim().toLowerCase().min(1, 'Обязательное поле'),
  password: z.string().min(1, 'Обязательное поле'),
});
type FormData = z.infer<typeof schema>;

export default function LoginPage() {
  const navigate = useNavigate();
  const returnTo = getSafeReturnTo(window.location.search);
  const { login, setSession, isAuthenticated } = useAuthStore();
  const [serverError, setServerError] = useState('');
  const [passkeyBusy, setPasskeyBusy] = useState(false);
  const [passkeySupported, setPasskeySupported] = useState(false);
  // 2FA challenge state. When the server answers a password login with
  // TOTP_CODE_REQUIRED we open the reusable TotpModal and resubmit with the code.
  const [totpOpen, setTotpOpen] = useState(false);
  const [totpErrorCode, setTotpErrorCode] = useState<'TOTP_CODE_REQUIRED' | 'TOTP_INVALID' | null>(null);
  const [totpError, setTotpError] = useState<string | null>(null);
  const [totpLoading, setTotpLoading] = useState(false);
  const pendingCreds = useRef<FormData | null>(null);

  useEffect(() => {
    setPasskeySupported(webauthnApi.isSupported());
  }, []);

  useEffect(() => {
    if (isAuthenticated && returnTo) window.location.replace(returnTo);
  }, [isAuthenticated, returnTo]);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<FormData>({ resolver: zodResolver(schema) });

  const goAfterLogin = () => {
    if (returnTo) {
      window.location.replace(returnTo);
      return;
    }
    const hasVisited = localStorage.getItem('bogema-settings-visited');
    if (!hasVisited) {
      localStorage.setItem('bogema-settings-visited', '1');
      navigate('/settings');
    } else {
      navigate('/');
    }
  };

  const onPasskeyLogin = async () => {
    setServerError('');
    setPasskeyBusy(true);
    try {
      const hint = (watch('emailOrUsername') ?? '').trim();
      const { accessToken } = await webauthnApi.login(hint || undefined);
      // Fetch full /me so bio/status/createdAt populate the store identically
      // to a regular login. setSession кладёт токен в память (#1 — не localStorage).
      const me = await authApi.me();
      setSession(accessToken, me);
      goAfterLogin();
    } catch (err) {
      const msg = extractError(err);
      if (/NotAllowed|cancel|aborted/i.test(msg)) {
        setServerError('Операция отменена');
      } else {
        setServerError(msg);
      }
    } finally {
      setPasskeyBusy(false);
    }
  };

  const onSubmit = async (data: FormData) => {
    setServerError('');
    try {
      await login(data.emailOrUsername, data.password);
      goAfterLogin();
    } catch (err) {
      const api = extractApiError(err);
      if (api.code === 'TOTP_CODE_REQUIRED') {
        // Password accepted but the account has 2FA — collect the code and retry.
        pendingCreds.current = data;
        setTotpErrorCode('TOTP_CODE_REQUIRED');
        setTotpError(null);
        setTotpOpen(true);
        return;
      }
      setServerError(extractError(err));
    }
  };

  // Retry login with the 2FA code from the modal. Keeps the modal open on a
  // wrong code (TOTP_INVALID) so the user can try again.
  const onTotpSubmit = async (code: string) => {
    const creds = pendingCreds.current;
    if (!creds) return;
    setTotpLoading(true);
    setTotpError(null);
    try {
      await login(creds.emailOrUsername, creds.password, code);
      setTotpOpen(false);
      goAfterLogin();
    } catch (err) {
      const api = extractApiError(err);
      if (api.code === 'TOTP_INVALID') {
        setTotpErrorCode('TOTP_INVALID');
        setTotpError('Неверный код двухфакторной аутентификации');
      } else if (api.code === 'TOTP_CODE_REQUIRED') {
        setTotpErrorCode('TOTP_CODE_REQUIRED');
        setTotpError(null);
      } else {
        // Unrelated failure — close the modal and surface it on the main form.
        setTotpOpen(false);
        setServerError(extractError(err));
      }
    } finally {
      setTotpLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-surface-950 flex items-center justify-center p-4">
      <div className="w-full max-w-sm">
        <div className="text-center mb-8">
          <SwipaWordmark size={44} base="#ffffff" accent="#C5243A" />
          <p className="text-surface-400 mt-3 text-sm">Войти в аккаунт</p>
        </div>

        <form
          onSubmit={handleSubmit(onSubmit)}
          className="bg-surface-900 border border-surface-800 rounded-xl p-6 space-y-4"
        >
          {serverError && (
            <div className="bg-red-900/40 border border-red-700 text-red-300 text-sm rounded-lg px-3 py-2">
              {serverError}
            </div>
          )}

          <div>
            <label className="block text-surface-300 text-xs font-medium mb-1.5">
              Email или имя пользователя
            </label>
            <input
              {...register('emailOrUsername')}
              className="irc-input lowercase"
              placeholder="ivan_ivanov"
              autoComplete="username"
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
            />
            {errors.emailOrUsername && (
              <p className="text-red-400 text-xs mt-1">{errors.emailOrUsername.message}</p>
            )}
          </div>

          <div>
            <label className="block text-surface-300 text-xs font-medium mb-1.5">Пароль</label>
            <input
              {...register('password')}
              type="password"
              className="irc-input"
              placeholder="••••••••"
              autoComplete="current-password"
            />
            {errors.password && (
              <p className="text-red-400 text-xs mt-1">{errors.password.message}</p>
            )}
          </div>

          <div className="flex justify-end">
            <Link
              to="/auth/forgot-password"
              className="text-brand-400 hover:text-brand-300 text-xs"
            >
              Забыли пароль?
            </Link>
          </div>

          <button type="submit" disabled={isSubmitting} className="btn-primary w-full">
            {isSubmitting ? (
              <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            ) : (
              'Войти'
            )}
          </button>

          {passkeySupported && (
            <>
              <div className="flex items-center gap-3 text-surface-600 text-xs">
                <div className="flex-1 h-px bg-surface-800" />
                или
                <div className="flex-1 h-px bg-surface-800" />
              </div>
              <button
                type="button"
                onClick={onPasskeyLogin}
                disabled={passkeyBusy}
                className="w-full flex items-center justify-center gap-2 py-2 px-4 rounded-lg bg-surface-800 border border-surface-700 hover:border-brand-500 text-surface-100 text-sm font-medium transition-colors disabled:opacity-50"
              >
                <Fingerprint size={16} className="text-brand-400" />
                {passkeyBusy ? 'Подождите…' : 'Войти с помощью ключа доступа'}
              </button>
            </>
          )}

          <p className="text-center text-surface-400 text-sm">
            Нет аккаунта?{' '}
            <Link to={withReturnTo('/auth/register', returnTo)} className="text-brand-400 hover:text-brand-300">
              Зарегистрироваться
            </Link>
          </p>
        </form>
      </div>

      <TotpModal
        open={totpOpen}
        onClose={() => setTotpOpen(false)}
        onSubmit={onTotpSubmit}
        errorCode={totpErrorCode}
        errorMessage={totpError}
        loading={totpLoading}
        actionLabel="Подтверждение входа"
      />
    </div>
  );
}
